import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  MEDIA_VIEWER_SWIPE_THRESHOLD_PX,
  swipeDirection,
} from "./mediaViewerSwipe";

export { MEDIA_VIEWER_SWIPE_THRESHOLD_PX, swipeDirection };

const DEFAULT_TEST_IDS = {
  root: "shared-media-viewer",
  backdrop: "shared-media-viewer-backdrop",
  toolbar: "shared-media-viewer-toolbar",
  canvas: "shared-media-viewer-canvas",
  loading: "shared-media-viewer-loading",
  error: "shared-media-viewer-error",
  image: "shared-media-viewer-image",
  navPrev: "shared-media-viewer-nav-prev",
  navNext: "shared-media-viewer-nav-next",
  preloadPrev: "shared-media-viewer-preload-prev",
  preloadNext: "shared-media-viewer-preload-next",
  counter: "shared-media-viewer-counter",
};

function readPrefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return false;
  }
}

function clampIndex(index, length) {
  if (!length) return 0;
  if (!Number.isFinite(Number(index))) return 0;
  return Math.min(Math.max(Number(index), 0), length - 1);
}

function SharedMediaViewer({
  items,
  activeIndex,
  onClose,
  onIndexChange,
  allowDownload = true,
  restoreFocusRef = null,
  title = "Перегляд зображення",
  testIds = null,
}) {
  const ids = { ...DEFAULT_TEST_IDS, ...(testIds || {}) };
  const titleDomId = useId();
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const touchStartRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onIndexChangeRef = useRef(onIndexChange);
  const itemsRef = useRef(items);
  const indexRef = useRef(activeIndex);
  const [canvasLoading, setCanvasLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(readPrefersReducedMotion);

  onCloseRef.current = onClose;
  onIndexChangeRef.current = onIndexChange;
  itemsRef.current = items;
  indexRef.current = activeIndex;

  const safeItems = Array.isArray(items) ? items : [];
  const closed = activeIndex == null || activeIndex < 0;
  const open = !closed && safeItems.length > 0;
  const safeIndex = open ? clampIndex(activeIndex, safeItems.length) : 0;
  const current = open ? safeItems[safeIndex] : null;
  const hasNav = open && safeItems.length > 1;
  const prevItem = hasNav
    ? safeItems[(safeIndex - 1 + safeItems.length) % safeItems.length]
    : null;
  const nextItem = hasNav
    ? safeItems[(safeIndex + 1) % safeItems.length]
    : null;
  const downloadHref = current?.downloadUrl || current?.src || "";
  const showDownload = Boolean(allowDownload && downloadHref);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    let media;
    try {
      media = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return undefined;
    }
    if (!media) return undefined;
    const sync = () => setReducedMotion(Boolean(media.matches));
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    if (typeof media.addListener === "function") {
      media.addListener(sync);
      return () => media.removeListener(sync);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (closed) return undefined;
    if (!safeItems.length) {
      onCloseRef.current?.();
      return undefined;
    }
    if (activeIndex !== safeIndex) {
      onIndexChangeRef.current?.(safeIndex);
    }
    return undefined;
  }, [closed, safeItems.length, activeIndex, safeIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    closeBtnRef.current?.focus();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const trigger = restoreFocusRef?.current;
    return () => {
      if (trigger && typeof trigger.focus === "function" && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [open, restoreFocusRef]);

  useEffect(() => {
    setCanvasLoading(true);
    setLoadError(false);
  }, [current?.id, current?.src]);

  const showPrev = () => {
    const list = itemsRef.current;
    if (!Array.isArray(list) || list.length < 2) return;
    const index = clampIndex(indexRef.current, list.length);
    onIndexChangeRef.current?.((index - 1 + list.length) % list.length);
  };

  const showNext = () => {
    const list = itemsRef.current;
    if (!Array.isArray(list) || list.length < 2) return;
    const index = clampIndex(indexRef.current, list.length);
    onIndexChangeRef.current?.((index + 1) % list.length);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        onCloseRef.current?.();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPrev();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        showNext();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  const handleTouchStart = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (event) => {
    if (!touchStartRef.current) return;
    const touch = event.changedTouches?.[0] || event.touches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      event.preventDefault();
    }
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const touch = event.changedTouches?.[0];
    if (!start || !touch) return;
    const direction = swipeDirection(
      touch.clientX - start.x,
      touch.clientY - start.y,
      MEDIA_VIEWER_SWIPE_THRESHOLD_PX,
    );
    if (direction === "next") showNext();
    if (direction === "prev") showPrev();
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'button:not([disabled]), a[href]',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open || !current || typeof document === "undefined") {
    return null;
  }

  const className = reducedMotion
    ? "shared-media-viewer shared-media-viewer--reduced-motion"
    : "shared-media-viewer";

  return createPortal(
    <div
      className={className}
      data-testid={ids.root}
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      <div
        className="shared-media-viewer__backdrop"
        data-testid={ids.backdrop}
        onClick={() => onCloseRef.current?.()}
      />
      <div
        ref={dialogRef}
        className="shared-media-viewer__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleDomId}
        onKeyDown={handleDialogKeyDown}
      >
        <h2 id={titleDomId} className="shared-media-viewer__title">
          {title}
        </h2>
        <div className="shared-media-viewer__toolbar" data-testid={ids.toolbar}>
          {showDownload ? (
            <a
              className="shared-media-viewer__control"
              href={downloadHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Завантажити"
              title="Завантажити"
              download={current.filename || true}
            >
              <i className="fa-solid fa-download" aria-hidden="true" />
            </a>
          ) : null}
          <button
            ref={closeBtnRef}
            type="button"
            className="shared-media-viewer__control"
            aria-label="Закрити"
            title="Закрити"
            onClick={() => onCloseRef.current?.()}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <div
          className="shared-media-viewer__canvas"
          data-testid={ids.canvas}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {canvasLoading && !loadError ? (
            <div
              className="shared-media-viewer__loading"
              data-testid={ids.loading}
              role="status"
            >
              Завантаження…
            </div>
          ) : null}
          {loadError ? (
            <div
              className="shared-media-viewer__error"
              data-testid={ids.error}
              role="alert"
            >
              Не вдалося завантажити зображення
            </div>
          ) : (
            <img
              key={`${current.id}:${current.src}`}
              src={current.src}
              alt={current.alt || ""}
              className="shared-media-viewer__image"
              data-testid={ids.image}
              onLoad={() => {
                setCanvasLoading(false);
                setLoadError(false);
              }}
              onError={() => {
                setCanvasLoading(false);
                setLoadError(true);
              }}
            />
          )}
          {hasNav ? (
            <>
              <button
                type="button"
                className="shared-media-viewer__nav shared-media-viewer__nav--prev"
                data-testid={ids.navPrev}
                aria-label="Попереднє зображення"
                title="Попереднє зображення"
                onClick={showPrev}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="shared-media-viewer__nav shared-media-viewer__nav--next"
                data-testid={ids.navNext}
                aria-label="Наступне зображення"
                title="Наступне зображення"
                onClick={showNext}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </button>
            </>
          ) : null}
          {hasNav ? (
            <p
              className="shared-media-viewer__counter"
              data-testid={ids.counter}
              aria-live="polite"
            >
              {safeIndex + 1} / {safeItems.length}
            </p>
          ) : null}
          {prevItem ? (
            <img
              src={prevItem.src}
              alt=""
              className="shared-media-viewer__preload"
              data-testid={ids.preloadPrev}
              aria-hidden="true"
            />
          ) : null}
          {nextItem ? (
            <img
              src={nextItem.src}
              alt=""
              className="shared-media-viewer__preload"
              data-testid={ids.preloadNext}
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default SharedMediaViewer;
